import React from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, X } from "lucide-react";
import type { SimpleColumn } from "@/lib/column-manager";

interface ShownColumnsListProps {
  /** Visible columns already in display (columnOrder) sequence. */
  shownColumns: SimpleColumn[];
  /** Reorder callback — receives the new visible-id sequence after a drag/keyboard move. */
  onReorder: (reorderedVisibleIds: string[]) => void;
  /** Hide a column (removes it from this pane). */
  onRemove: (columnId: string) => void;
}

interface SortableColumnRowProps {
  column: SimpleColumn;
  onRemove: (columnId: string) => void;
  /** True when it is the last visible column — removing it would blank the table. */
  isLastRemaining: boolean;
}

function SortableColumnRow({ column, onRemove, isLastRemaining }: SortableColumnRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: column.id,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      role="listitem"
      data-testid={`shown-column-${column.id}`}
      className="flex items-center gap-2 rounded-lg border border-border bg-card px-2 py-2"
    >
      <button
        type="button"
        className="touch-manipulation cursor-grab active:cursor-grabbing rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        aria-label={`Reorder ${column.label}`}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" aria-hidden />
      </button>
      <span className="flex-1 truncate text-sm text-foreground">{column.label}</span>
      <button
        type="button"
        onClick={() => onRemove(column.id)}
        disabled={isLastRemaining}
        className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-40"
        aria-label={isLastRemaining ? `${column.label} is the last visible column` : `Hide ${column.label}`}
        title={isLastRemaining ? "At least one column must stay visible" : "Hide column"}
      >
        <X className="h-4 w-4" aria-hidden />
      </button>
    </div>
  );
}

/**
 * The "Shown columns" pane: the visible columns as an ordered, drag-to-reorder
 * list. Order here IS the table's left-to-right column order (persisted as
 * `columnOrder`). Removing a row hides that column (moves it back to the left
 * "available" pane); dragging reorders.
 */
export function ShownColumnsList({ shownColumns, onReorder, onRemove }: ShownColumnsListProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const ids = React.useMemo(() => shownColumns.map((c) => c.id), [shownColumns]);

  const handleDragEnd = React.useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = ids.indexOf(String(active.id));
      const newIndex = ids.indexOf(String(over.id));
      if (oldIndex === -1 || newIndex === -1) return;
      onReorder(arrayMove(ids, oldIndex, newIndex));
    },
    [ids, onReorder]
  );

  if (shownColumns.length === 0) {
    return (
      <div className="flex h-full min-h-[8rem] items-center justify-center rounded-lg border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
        No columns shown. Check a column on the left to add it here.
      </div>
    );
  }

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <div className="flex flex-col gap-1.5" role="list" aria-label="Shown columns in display order">
          {shownColumns.map((column) => (
            <SortableColumnRow
              key={column.id}
              column={column}
              onRemove={onRemove}
              isLastRemaining={shownColumns.length === 1}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
